/**
 * HOOK LAB — CLAUDECODE101 chapter 9, attaching one rule and watching where it lands.
 *
 * FOUNDER, 2026-08-15: "i want claude code chapters to deep dive".
 *
 * ── AN ASYMMETRY CAN BE RUN; IT CANNOT BE ASSERTED INTO SOMEBODY ──────────────────────────────
 * §5 says "a log is not a guard". Reading that produces agreement. Placing a rule on PostToolUse,
 * watching the `git clean` complete, and being handed a perfect record of destroyed work produces
 * the lesson. That is the whole reason this chapter gets an instrument when most do not.
 *
 * ── NOTHING IS SCORED, AND HERE THERE IS NOT EVEN A RIGHT ANSWER ──────────────────────────────
 * Two of the six actions are recoverable, and for those a rule on PostToolUse is the correct
 * engineering answer — it is what this machine actually does. The lab says so in plain words. A lab
 * that marked it down would teach learners to block work that should flow, which is chapter 2's
 * reflex-approval failure arriving from the other direction.
 *
 * ── AND ONE PLACEMENT RETURNS "NOT KNOWN" ─────────────────────────────────────────────────────
 * Notification's position in a tool call is not established by anything this course can cite, so
 * placing a rule there returns an abstention rather than a guess. In a ladder that teaches the
 * fourth kind of claim, the instrument must not manufacture one.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  EVENTS, ACTIONS, NOWHERE, traceable, actionOf, trace, outcome,
  THE_AXIS, A_LOG_IS_NOT_A_GUARD, THE_REVERSE, WHY_REAL, NOT_ALWAYS_PRE, SOURCE,
} from './hookchain.mjs';

export default function HookLab() {
  const [actionId, setActionId] = useState(null);
  const [placement, setPlacement] = useState(null);

  const action = actionOf(actionId);
  const run = action && placement ? trace(actionId, placement) : null;
  const result = action && placement ? outcome(actionId, placement) : null;

  const reset = () => { setActionId(null); setPlacement(null); };
  const pickAction = (id) => { setActionId(id); setPlacement(null); };

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Hook lab · one rule, six places to put it</div>
      <h2 className="g-h2 lab-h">Five events tell you what happened. One decides whether it happens.</h2>
      <p className="g-body">
        <Value of={cited('PreToolUse is prevention. PostToolUse is evidence. You want both, and confusing one for the other is a real mistake with a real cost. A log is not a guard.', SOURCE)} />
      </p>
      <p className="g-body g-dim">{WHY_REAL}</p>

      {/* ── THE CHAIN, ALWAYS ON SCREEN ── */}
      <div className="hk-chain">
        {traceable().map((e) => (
          <div key={e.id} className={`hk-ev ${e.can_refuse ? 'hk-refuses' : ''}`}>
            <b>{e.id}</b>
            <span className="g-label">{e.matchers} matcher{e.matchers === 1 ? '' : 's'}</span>
            <p className="g-body g-dim">{e.when}</p>
          </div>
        ))}
      </div>
      {/* An event with no established position is drawn apart, not hidden and not placed. */}
      {EVENTS.filter((e) => e.untraceable).map((e) => (
        <p key={e.id} className="g-body hk-aside">
          <b>{e.id}</b> is wired here too, and is not on the line above. §4 says only that it fires
          “when it wants your attention” — which is not a fixed point in a tool call. Where a rule
          there would run relative to a given action is not established, so this lab does not draw it.
        </p>
      ))}

      {/* ── STEP 1 · THE ACTION ── */}
      <div className="lab-task">
        <div className="g-label">1 · Choose an action. All six are real ones from this estate.</div>
        <div className="hk-actions">
          {ACTIONS.map((a) => (
            <button key={a.id} className={`g-btn hk-act ${actionId === a.id ? 'hk-on' : ''}`}
              onClick={() => pickAction(a.id)}>
              <span className="g-label">{a.tool}</span>
              {a.what}
            </button>
          ))}
        </div>
      </div>

      {/* ── STEP 2 · THE PLACEMENT ── */}
      {action && (
        <div className="lab-task">
          <div className="g-label">2 · Attach one rule. Where does it go?</div>
          <p className="g-body g-dim hk-recover">
            <b>Before you choose:</b> if this happens, can it be taken back? {action.recovery}
          </p>
          <div className="lab-task-head">
            {EVENTS.map((e) => (
              <button key={e.id} className={`g-btn ${placement === e.id ? 'hk-on' : ''}`}
                onClick={() => setPlacement(e.id)}>{e.id}</button>
            ))}
            <button className={`g-btn ${placement === NOWHERE ? 'hk-on' : ''}`}
              onClick={() => setPlacement(NOWHERE)}>nowhere</button>
          </div>
        </div>
      )}

      {/* ── THE TRACE ── */}
      {run && (
        <div className="lab-task">
          <div className="g-label">What ran</div>
          <div className="hk-trace">
            {run.steps.map((s, i) => (
              <div key={i} className={`hk-step ${s.mine ? 'hk-mine' : ''} ${s.refused ? 'hk-stop' : ''} ${s.happened ? 'hk-did' : ''}`}>
                <b>{s.event || (s.refused ? '⟂ refused' : '▸ the action')}</b>
                <span className="g-body">{s.note}</span>
              </div>
            ))}
            {!run.stopped && <div className="hk-step hk-end"><b>turn ends</b><span className="g-body">nothing further can intervene</span></div>}
          </div>

          {/* ── THE OUTCOME ── */}
          <div className={`hk-out hk-${result.kind}`}>
            <div className="lab-readout">
              <div><span className="g-label">Happened</span>
                <Value of={result.happened === null ? refused('not established') : computed(result.happened ? 'yes' : 'no')} /></div>
              <div><span className="g-label">Recorded</span>
                <Value of={result.recorded === null ? refused('not established') : computed(result.recorded ? 'yes' : 'no')} /></div>
              <div><span className="g-label">Can be taken back</span>
                <Value of={computed(action.recoverable ? 'yes' : 'no')} /></div>
            </div>
            <p className="g-body hk-headline"><b>{result.headline}</b></p>
            <p className="g-body">{result.reading}</p>
          </div>

          {/* ── WHERE IT REALLY SITS ── */}
          <div className="hk-real">
            <div className="g-label">Where the rule actually sits on this machine</div>
            {action.real.map((r) => (
              <p key={r.event + r.hook} className="g-body">
                <b>{r.event}</b> — <code>{r.hook}</code><br />
                <span className="g-dim">{r.job}</span>
              </p>
            ))}
            {action.real.length > 1 && (
              <p className="g-body hk-punch">
                One script, {action.real.length} events, {action.real.length} different jobs. The
                same code refuses, records, and briefs — and which of those it is doing is decided
                entirely by where it is attached. That is this chapter in one artefact.
              </p>
            )}
            {/* Read from the script, or counted from the settings file — never the one presented as
                the other. Chapter 16 §3 is the reason this is two branches and not one. */}
            <p className="g-body g-dim">
              {action.quote
                ? <Value of={cited(action.quote, action.quote_from)} />
                : <Value of={computed(action.counted)} />}
            </p>
            {action.counted_by && <p className="g-body g-dim"><b>How that was counted:</b> {action.counted_by}</p>}
          </div>

          <button className="g-btn" onClick={reset}>Try another</button>
        </div>
      )}

      <p className="g-body clm-note"><b>PreToolUse is not the answer to everything.</b> {NOT_ALWAYS_PRE}</p>

      <p className="g-body g-dim lab-why">
        {THE_AXIS}
        <br />
        Nothing here is scored, and for two of these six actions there is no wrong placement to
        score — a rule on <code>PostToolUse</code> is what this machine really does, deliberately.
        A lab that marked that down would teach you to block work that should have flowed.
        <br />
        {A_LOG_IS_NOT_A_GUARD} {THE_REVERSE}
      </p>
    </div>
  );
}
